import { useMemo } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const STATUS_DOT = {
  proposed: "bg-blue-500",
  confirmed: "bg-emerald-500",
  attended: "bg-indigo-500",
  no_show: "bg-amber-500",
  paid: "bg-emerald-600",
  rescheduled: "bg-yellow-500",
  canceled: "bg-rose-400",
  done: "bg-slate-500",
};

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function CalendarView({ appointments, customers, services, cursor, setCursor, onSelectDay, onSelectAppointment }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const today = new Date();

  const grid = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const startWeekday = firstOfMonth.getDay(); // 0-6 (Sun first)
    const gridStart = new Date(year, month, 1 - startWeekday);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [year, month]);

  const byDay = useMemo(() => {
    const map = {};
    for (const a of appointments) {
      const d = new Date(a.scheduled_at);
      const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[k] ||= []).push(a);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
    }
    return map;
  }, [appointments]);

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const serviceName = (code) => services.find((s) => s.code === code)?.name || code;

  const prev = () => setCursor(new Date(year, month - 1, 1));
  const next = () => setCursor(new Date(year, month + 1, 1));
  const goToday = () => setCursor(new Date());

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2">
          <button onClick={prev} data-testid="calendar-prev" className="p-1.5 rounded hover:bg-slate-200 text-slate-700"><CaretLeft size={16} weight="bold" /></button>
          <button onClick={next} data-testid="calendar-next" className="p-1.5 rounded hover:bg-slate-200 text-slate-700"><CaretRight size={16} weight="bold" /></button>
          <button onClick={goToday} data-testid="calendar-today" className="ml-1 text-xs font-medium text-slate-700 hover:bg-slate-200 px-2 py-1 rounded">Hoje</button>
        </div>
        <div className="font-heading text-lg font-semibold text-slate-900" data-testid="calendar-title">{MONTHS[month]} {year}</div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-full" /> proposto</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full" /> confirmado</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-600 rounded-full" /> pago</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-full" /> no-show</span>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider text-center">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {grid.map((d, i) => {
          const inMonth = d.getMonth() === month;
          const isToday = sameDay(d, today);
          const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const items = byDay[k] || [];
          return (
            <div
              key={i}
              onClick={() => onSelectDay && onSelectDay(d)}
              data-testid={`calendar-day-${d.toISOString().slice(0, 10)}`}
              className={`min-h-[110px] border-b border-r border-slate-100 p-2 cursor-pointer transition-colors hover:bg-blue-50/40 ${inMonth ? "bg-white" : "bg-slate-50/60"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-medium ${isToday ? "bg-blue-700 text-white w-6 h-6 inline-flex items-center justify-center rounded-full" : inMonth ? "text-slate-900" : "text-slate-400"}`}>
                  {d.getDate()}
                </span>
                {items.length > 3 && (
                  <span className="text-[10px] text-slate-500">+{items.length - 3}</span>
                )}
              </div>
              <div className="space-y-1">
                {items.slice(0, 3).map((a) => (
                  <button
                    key={a.id}
                    onClick={(e) => { e.stopPropagation(); onSelectAppointment && onSelectAppointment(a); }}
                    title={`${new Date(a.scheduled_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} — ${customerName(a.customer_id)} — ${serviceName(a.service_code)}`}
                    className="w-full text-left text-[11px] leading-tight px-1.5 py-1 rounded border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50 flex items-center gap-1.5 truncate"
                    data-testid={`calendar-appt-${a.id}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[a.status] || "bg-slate-400"}`} />
                    <span className="font-medium text-slate-700 tabular-nums">
                      {new Date(a.scheduled_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span className="text-slate-600 truncate">{customerName(a.customer_id)}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
